import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Printer, Package, Banknote, ShoppingCart, Trophy, BarChart2, Tag, Flame, CheckCircle2, ArrowRight, Clock, Moon, Ban, Search, Inbox, Lightbulb, Store, FileText, Phone, Mail, ArrowDownCircle, ArrowUpCircle, RotateCcw, History, ChevronDown, Filter } from 'lucide-react';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import logger from '../utils/logger';

const BusinessSalesAnalytics = ({ projectMode = false }) => {
  const routeBase = useWorkspaceBasePath();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('30j');
  const [customDateDebut, setCustomDateDebut] = useState('');
  const [customDateFin, setCustomDateFin] = useState('');
  const [flowData, setFlowData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [categories, setCategories] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterClassification, setFilterClassification] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('total_vendu');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [settings, setSettings] = useState(null);
  const printRef = useRef();
  const itemsPerPage = 15;

  // États pour l'onglet Historique mouvements
  const [movementData, setMovementData] = useState(null);
  const [movementProducts, setMovementProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [movementTypeFilter, setMovementTypeFilter] = useState('');
  const [movementDateDebut, setMovementDateDebut] = useState('');
  const [movementDateFin, setMovementDateFin] = useState('');
  const [movementPage, setMovementPage] = useState(1);
  const [movementLoading, setMovementLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // Fermer le dropdown produit au clic extérieur
  useEffect(() => {
    const handleClickOutside = () => setShowProductDropdown(false);
    if (showProductDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showProductDropdown]);

  const buildParams = useCallback(() => {
    const params = { period };
    if (period === 'custom' && customDateDebut && customDateFin) {
      params.date_debut = customDateDebut;
      params.date_fin = customDateFin;
    }
    return params;
  }, [period, customDateDebut, customDateFin]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [flowRes, summaryRes, alertsRes, settingsRes] = await Promise.all([
        api.get('/business/sales-analytics', { params }),
        api.get('/business/sales-analytics/summary'),
        api.get('/business/sales-analytics/alerts'),
        api.get('/business/settings').catch(() => ({ data: null }))
      ]);
      setFlowData(flowRes.data.data);
      setSummary(summaryRes.data.data);
      setAlerts(alertsRes.data.data);
      if (settingsRes.data) setSettings(settingsRes.data);
      setCurrentPage(1);
    } catch (err) {
      logger.error('Erreur chargement analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadTrends = useCallback(async () => {
    try {
      const params = buildParams();
      const res = await api.get('/business/sales-analytics/trends', { params });
      setTrends(res.data.data);
    } catch (err) {
      logger.error('Erreur chargement tendances:', err);
    }
  }, [buildParams]);

  const loadCategories = useCallback(async () => {
    try {
      const params = buildParams();
      const res = await api.get('/business/sales-analytics/categories', { params });
      setCategories(res.data.data);
    } catch (err) {
      logger.error('Erreur chargement catégories:', err);
    }
  }, [buildParams]);

  const loadMovementProducts = useCallback(async () => {
    try {
      const res = await api.get('/business/sales-analytics/products-movements');
      setMovementProducts(res.data.data || []);
    } catch (err) {
      logger.error('Erreur chargement liste produits mouvements:', err);
    }
  }, []);

  const loadMovements = useCallback(async (page = 1) => {
    setMovementLoading(true);
    try {
      const params = { page, limit: 30 };
      if (selectedProductId) params.product_id = selectedProductId;
      if (movementTypeFilter) params.type = movementTypeFilter;
      if (movementDateDebut) params.date_debut = movementDateDebut;
      if (movementDateFin) params.date_fin = movementDateFin;
      const res = await api.get('/business/sales-analytics/movements', { params });
      setMovementData(res.data.data);
      setMovementPage(page);
    } catch (err) {
      logger.error('Erreur chargement mouvements:', err);
    } finally {
      setMovementLoading(false);
    }
  }, [selectedProductId, movementTypeFilter, movementDateDebut, movementDateFin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'trends') loadTrends();
    if (activeTab === 'categories') loadCategories();
    if (activeTab === 'movements') {
      loadMovementProducts();
      loadMovements(1);
    }
  }, [activeTab, loadTrends, loadCategories, loadMovementProducts, loadMovements]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setCurrentPage(1);
  };

  // Impression
  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  // Filtrer et trier les produits
  const getFilteredProducts = () => {
    if (!flowData?.products) return [];
    let products = [...flowData.products];

    if (filterClassification !== 'all') {
      products = products.filter(p => p.classification === filterClassification);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      products = products.filter(p =>
        p.product_name?.toLowerCase().includes(term) ||
        p.categorie?.toLowerCase().includes(term)
      );
    }

    products.sort((a, b) => {
      const valA = parseFloat(a[sortBy]) || 0;
      const valB = parseFloat(b[sortBy]) || 0;
      return sortOrder === 'desc' ? valB - valA : valA - valB;
    });

    return products;
  };

  const formatNumber = (n) => {
    if (n === null || n === undefined) return '0';
    return new Intl.NumberFormat('fr-FR').format(n);
  };

  const formatMoney = (n) => {
    if (n === null || n === undefined) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';
  };

  const classificationOptions = [
    { value: 'all', label: 'Tous', icon: '' },
    { value: 'best_seller', label: 'Best-sellers', icon: '' },
    { value: 'bon', label: 'Bons', icon: '' },
    { value: 'moyen', label: 'Moyens', icon: '' },
    { value: 'lent', label: 'Lents', icon: '' },
    { value: 'dormant', label: 'Dormants', icon: '' },
    { value: 'jamais_vendu', label: 'Jamais vendus', icon: '' }
  ];

  const periodOptions = [
    { value: '7j', label: '7 jours' },
    { value: '30j', label: '30 jours' },
    { value: '90j', label: '3 mois' },
    { value: 'annee', label: 'Cette année' },
    { value: 'all', label: 'Tout' },
    { value: 'custom', label: 'Personnalisé' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Analyse des ventes en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1"><TrendingUp size={24} className="inline mr-1" /> Analyse des Ventes</h1>
          <p className="text-gray-500 text-sm">Flux de ventes, classement des produits et alertes de stock</p>
        </div>
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 text-sm font-medium shadow-sm"
        >
          <Printer size={16} className="inline mr-1" /> Imprimer
        </button>
      </div>

      {/* Résumé rapide */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl mb-1"><Package size={24} /></div>
            <div className="text-xs text-gray-500">Unités vendues (30j)</div>
            <div className="text-xl font-bold text-gray-800">{formatNumber(summary.ventes_30j)}</div>
            {summary.variation_ventes !== 0 && (
              <div className={`text-xs mt-1 font-medium ${summary.variation_ventes > 0 ? 'text-green-600' : 'text-red-500'}`}>
                {summary.variation_ventes > 0 ? '↑' : '↓'} {Math.abs(summary.variation_ventes)}% vs mois précédent
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl mb-1"><Banknote size={24} /></div>
            <div className="text-xs text-gray-500">CA (30 jours)</div>
            <div className="text-xl font-bold text-gray-800">{formatMoney(summary.ca_30j)}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl mb-1"><ShoppingCart size={24} /></div>
            <div className="text-xs text-gray-500">Commandes (30j)</div>
            <div className="text-xl font-bold text-gray-800">{formatNumber(summary.commandes_30j)}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl mb-1"><Trophy size={24} /></div>
            <div className="text-xs text-gray-500">Meilleur produit</div>
            <div className="text-sm font-bold text-gray-800 truncate">
              {summary.top_produit?.nom || '-'}
            </div>
            {summary.top_produit && (
              <div className="text-xs text-gray-500">{formatNumber(summary.top_produit.total_vendu)} vendus</div>
            )}
          </div>
        </div>
      )}

      

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6 overflow-x-auto">
        {[
          { key: 'overview', label: 'Vue d\'ensemble', icon: <BarChart2 size={16} /> },
          { key: 'trends', label: 'Tendances', icon: <TrendingUp size={16} /> },
          { key: 'categories', label: 'Par catégorie', icon: <Tag size={16} /> },
          { key: 'movements', label: 'Historique mouvements', icon: <History size={16} /> }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center space-x-1 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Filtre période */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-sm text-gray-500 font-medium">Période :</span>
        {periodOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => handlePeriodChange(opt.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              period === opt.value
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={customDateDebut}
              onChange={(e) => setCustomDateDebut(e.target.value)}
              className="border rounded-lg px-2 py-1 text-xs"
            />
            <span className="text-gray-400">→</span>
            <input
              type="date"
              value={customDateFin}
              onChange={(e) => setCustomDateFin(e.target.value)}
              className="border rounded-lg px-2 py-1 text-xs"
            />
          </div>
        )}
      </div>

      {/* TAB: Vue d'ensemble */}
      {activeTab === 'overview' && (
        <>
          {/* Stats de classification */}
          {flowData?.stats && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
              <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
                <div className="text-lg font-bold text-gray-800">{flowData.stats.total_produits}</div>
                <div className="text-xs text-gray-500">Total produits</div>
              </div>
              <div className="bg-green-50 rounded-xl p-3 shadow-sm border border-green-200 text-center cursor-pointer hover:bg-green-100"
                onClick={() => setFilterClassification(filterClassification === 'best_seller' ? 'all' : 'best_seller')}>
                <div className="text-lg font-bold text-green-700">{flowData.stats.best_sellers}</div>
                <div className="text-xs text-green-600"><Flame size={14} className="inline mr-0.5" /> Best-sellers</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 shadow-sm border border-blue-200 text-center cursor-pointer hover:bg-blue-100"
                onClick={() => setFilterClassification(filterClassification === 'bon' ? 'all' : 'bon')}>
                <div className="text-lg font-bold text-blue-700">{flowData.stats.bons}</div>
                <div className="text-xs text-blue-600"><CheckCircle2 size={14} className="inline mr-0.5" /> Bons</div>
              </div>
              <div className="bg-yellow-50 rounded-xl p-3 shadow-sm border border-yellow-200 text-center cursor-pointer hover:bg-yellow-100"
                onClick={() => setFilterClassification(filterClassification === 'moyen' ? 'all' : 'moyen')}>
                <div className="text-lg font-bold text-yellow-700">{flowData.stats.moyens}</div>
                <div className="text-xs text-yellow-600"><ArrowRight size={14} className="inline mr-0.5" /> Moyens</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-3 shadow-sm border border-orange-200 text-center cursor-pointer hover:bg-orange-100"
                onClick={() => setFilterClassification(filterClassification === 'lent' ? 'all' : 'lent')}>
                <div className="text-lg font-bold text-orange-700">{flowData.stats.lents}</div>
                <div className="text-xs text-orange-600"><Clock size={14} className="inline mr-0.5" /> Lents</div>
              </div>
              <div className="bg-red-50 rounded-xl p-3 shadow-sm border border-red-200 text-center cursor-pointer hover:bg-red-100"
                onClick={() => setFilterClassification(filterClassification === 'dormant' ? 'all' : 'dormant')}>
                <div className="text-lg font-bold text-red-700">{flowData.stats.dormants}</div>
                <div className="text-xs text-red-600"><Moon size={14} className="inline mr-0.5" /> Dormants</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 shadow-sm border border-gray-200 text-center cursor-pointer hover:bg-gray-100"
                onClick={() => setFilterClassification(filterClassification === 'jamais_vendu' ? 'all' : 'jamais_vendu')}>
                <div className="text-lg font-bold text-gray-700">{flowData.stats.jamais_vendus}</div>
                <div className="text-xs text-gray-600"><Ban size={14} className="inline mr-0.5" /> Jamais vendus</div>
              </div>
            </div>
          )}

          {/* Barre de recherche + filtres */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 mb-4">
            <div className="relative flex-1 w-full">
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full border rounded-lg pl-9 pr-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <span className="absolute left-3 top-2.5 text-gray-400"><Search size={16} /></span>
            </div>
            <select
              value={filterClassification}
              onChange={(e) => { setFilterClassification(e.target.value); setCurrentPage(1); }}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              {classificationOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
              ))}
            </select>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="total_vendu-desc">Plus vendus</option>
              <option value="total_vendu-asc">Moins vendus</option>
              <option value="total_ca-desc">CA le plus élevé</option>
              <option value="benefice_total-desc">Meilleur bénéfice</option>
              <option value="marge_pct-desc">Meilleure marge</option>
              <option value="jours_depuis_derniere_vente-asc">Vente récente</option>
              <option value="jours_depuis_derniere_vente-desc">Pas de vente récente</option>
            </select>
          </div>

          {/* Tableau des produits */}
          {(() => {
            const allFiltered = getFilteredProducts();
            const totalPages = Math.ceil(allFiltered.length / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const paginatedProducts = allFiltered.slice(startIndex, startIndex + itemsPerPage);

            return (
              <>
                {/* Compteur résultats */}
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">
                    {allFiltered.length} produit{allFiltered.length > 1 ? 's' : ''} trouvé{allFiltered.length > 1 ? 's' : ''}
                    {totalPages > 1 && ` — Page ${currentPage} sur ${totalPages}`}
                  </p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* version desktop */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium">Produit</th>
                          <th className="text-center px-3 py-3 font-medium">Statut</th>
                          <th className="text-right px-3 py-3 font-medium">Vendus</th>
                          <th className="text-right px-3 py-3 font-medium">CA</th>
                          <th className="text-right px-3 py-3 font-medium">Marge</th>
                          <th className="text-right px-3 py-3 font-medium">Bénéfice</th>
                          <th className="text-right px-3 py-3 font-medium">Part %</th>
                          <th className="text-right px-3 py-3 font-medium">Stock</th>
                          <th className="text-right px-3 py-3 font-medium">Dernière vente</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedProducts.map((product, index) => (
                          <tr key={product.product_id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          {product.image ? (
                            <img src={product.image} alt="" className="w-8 h-8 rounded-lg object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center text-xs"><Package size={16} /></div>
                          )}
                          <div>
                            <div className="font-medium text-gray-800">{product.product_name || 'Produit supprimé'}</div>
                            {product.categorie && (
                              <div className="text-xs text-gray-500">{product.categorie}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-center px-3 py-3">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: product.classification_color + '20',
                            color: product.classification_color
                          }}
                        >
                          {product.classification_label}
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 font-semibold text-gray-800">{formatNumber(product.total_vendu)}</td>
                      <td className="text-right px-3 py-3 text-gray-700">{formatMoney(product.total_ca)}</td>
                      <td className="text-right px-3 py-3">
                        <span className={`font-medium ${product.marge_pct >= 30 ? 'text-green-600' : product.marge_pct >= 15 ? 'text-yellow-600' : 'text-red-500'}`}>
                          {product.marge_pct}%
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 font-medium text-gray-700">{formatMoney(product.benefice_total)}</td>
                      <td className="text-right px-3 py-3">
                        <div className="flex items-center justify-end space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${Math.min(product.part_ventes, 100)}%`,
                                backgroundColor: product.classification_color
                              }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600">{product.part_ventes}%</span>
                        </div>
                      </td>
                      <td className="text-right px-3 py-3">
                        <span className={`font-medium ${
                          product.stock_actuel <= (product.stock_minimum || 0) ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {product.stock_actuel ?? '-'}
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 text-xs text-gray-500">
                        {product.jours_depuis_derniere_vente !== null && product.jours_depuis_derniere_vente !== undefined
                          ? product.jours_depuis_derniere_vente === 0
                            ? "Aujourd'hui"
                            : `il y a ${product.jours_depuis_derniere_vente}j`
                          : '-'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* version mobile */}
            <div className="md:hidden divide-y divide-gray-100">
              {paginatedProducts.map((product, index) => (
                <div key={product.product_id || index} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      {product.image ? (
                        <img src={product.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center"><Package size={20} /></div>
                      )}
                      <div>
                        <div className="font-medium text-gray-800">{product.product_name || 'Produit supprimé'}</div>
                        {product.categorie && (
                          <div className="text-xs text-gray-500">{product.categorie}</div>
                        )}
                      </div>
                    </div>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{
                        backgroundColor: product.classification_color + '20',
                        color: product.classification_color
                      }}
                    >
                      {product.classification_label}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Vendus</div>
                      <div className="font-semibold">{formatNumber(product.total_vendu)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">CA</div>
                      <div className="font-semibold">{formatMoney(product.total_ca)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Marge</div>
                      <div className={`font-semibold ${product.marge_pct >= 30 ? 'text-green-600' : product.marge_pct >= 15 ? 'text-yellow-600' : 'text-red-500'}`}>
                        {product.marge_pct}%
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                    <span>Stock: {product.stock_actuel ?? '-'}</span>
                    <span>Part: {product.part_ventes}%</span>
                    <span>
                      {product.jours_depuis_derniere_vente === 0
                        ? "Aujourd'hui"
                        : product.jours_depuis_derniere_vente
                          ? `il y a ${product.jours_depuis_derniere_vente}j`
                          : '-'
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {allFiltered.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-4xl mb-3"><Inbox size={40} /></div>
                <p className="font-medium">Aucun produit trouvé</p>
                <p className="text-sm mt-1">
                  {searchTerm || filterClassification !== 'all'
                    ? 'Essayez de modifier vos filtres'
                    : 'Aucune vente enregistrée pour cette période'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-lg text-sm ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
              >
                ««
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg text-sm ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
              >
                ← Précédent
              </button>
              <div className="flex gap-1 items-center">
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  showPages.add(1);
                  showPages.add(totalPages);
                  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                    showPages.add(i);
                  }
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  sortedPages.forEach((pg, idx) => {
                    if (idx > 0 && pg - sortedPages[idx - 1] > 1) {
                      pages.push(<span key={`e-${pg}`} className="px-2 text-gray-400">...</span>);
                    }
                    pages.push(
                      <button
                        key={pg}
                        onClick={() => setCurrentPage(pg)}
                        className={`w-10 h-10 rounded-lg text-sm ${currentPage === pg ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                      >
                        {pg}
                      </button>
                    );
                  });
                  return pages;
                })()}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg text-sm ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
              >
                Suivant →
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 rounded-lg text-sm ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
              >
                »»
              </button>
            </div>
          )}
          </>
            );
          })()}

          {/* Résumé financier */}
          {flowData?.stats && flowData.stats.total_ca > 0 && (
            <div className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
              <h3 className="text-sm font-semibold text-indigo-800 mb-3"><Lightbulb size={16} className="inline mr-1" /> Résumé financier de la période</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-indigo-600">Quantité totale vendue</div>
                  <div className="text-lg font-bold text-indigo-800">{formatNumber(flowData.stats.total_vendu)}</div>
                </div>
                <div>
                  <div className="text-xs text-indigo-600">Chiffre d'affaires</div>
                  <div className="text-lg font-bold text-indigo-800">{formatMoney(flowData.stats.total_ca)}</div>
                </div>
                <div>
                  <div className="text-xs text-indigo-600">Bénéfice estimé</div>
                  <div className="text-lg font-bold text-green-700">{formatMoney(flowData.stats.total_benefice)}</div>
                </div>
                <div>
                  <div className="text-xs text-indigo-600">Produits actifs</div>
                  <div className="text-lg font-bold text-indigo-800">{flowData.stats.total_produits}</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* TAB: Tendances */}
      {activeTab === 'trends' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-semibold text-gray-800 mb-4"><TrendingUp size={16} className="inline mr-1" /> Tendances de ventes</h3>
          {trends ? (
            <div className="space-y-6">
              {/* Ventes boutique en ligne */}
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-3"><Store size={16} className="inline mr-1" /> Boutique en ligne</h4>
                {trends.shop && trends.shop.length > 0 ? (
                  <div className="space-y-2">
                    {trends.shop.map((item, i) => {
                      const maxQty = Math.max(...trends.shop.map(s => parseInt(s.quantite_totale) || 0), 1);
                      const width = ((parseInt(item.quantite_totale) || 0) / maxQty) * 100;
                      return (
                        <div key={i} className="flex items-center space-x-3">
                          <div className="w-24 text-xs text-gray-500 text-right shrink-0">{item.periode}</div>
                          <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(width, 5)}%` }}
                            >
                              <span className="text-xs text-white font-medium">
                                {formatNumber(item.quantite_totale)}
                              </span>
                            </div>
                          </div>
                          <div className="w-28 text-xs text-gray-600 text-right shrink-0">
                            {formatMoney(item.chiffre_affaires)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucune donnée de vente boutique pour cette période</p>
                )}
              </div>

              {/* Ventes factures */}
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-3"><FileText size={16} className="inline mr-1" /> Factures</h4>
                {trends.invoices && trends.invoices.length > 0 ? (
                  <div className="space-y-2">
                    {trends.invoices.map((item, i) => {
                      const maxQty = Math.max(...trends.invoices.map(s => parseInt(s.quantite_totale) || 0), 1);
                      const width = ((parseInt(item.quantite_totale) || 0) / maxQty) * 100;
                      return (
                        <div key={i} className="flex items-center space-x-3">
                          <div className="w-24 text-xs text-gray-500 text-right shrink-0">{item.periode}</div>
                          <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(width, 5)}%` }}
                            >
                              <span className="text-xs text-white font-medium">
                                {formatNumber(item.quantite_totale)}
                              </span>
                            </div>
                          </div>
                          <div className="w-28 text-xs text-gray-600 text-right shrink-0">
                            {formatMoney(item.chiffre_affaires)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucune donnée de factures pour cette période</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Par catégorie */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          {categories.length > 0 ? (
            categories.map((cat, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-800"><Tag size={16} className="inline mr-1" /> {cat.categorie}</h4>
                      <div className="text-xs text-gray-500 mt-1">
                        {cat.nb_produits} produit{cat.nb_produits > 1 ? 's' : ''} • {formatNumber(cat.total_vendu)} vendus
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-800">{formatMoney(cat.total_ca)}</div>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {cat.produits.map((prod, j) => (
                    <div key={j} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-400 w-5">{j + 1}.</span>
                        <div>
                          <div className="text-sm font-medium text-gray-800">{prod.product_name}</div>
                          <div className="text-xs text-gray-500">
                            Prix: {formatMoney(prod.prix_vente)} • Stock: {prod.stock_actuel ?? '-'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-800">{formatNumber(prod.total_vendu)} vendus</div>
                        <div className="text-xs text-gray-500">{formatMoney(prod.total_ca)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm">
              <div className="text-4xl mb-3"><Tag size={40} /></div>
              <p className="font-medium">Aucune donnée par catégorie</p>
              <p className="text-sm mt-1">Les ventes seront regroupées par catégorie de produit</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Historique mouvements */}
      {activeTab === 'movements' && (
        <div className="space-y-4">
          {/* Filtres */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={16} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700">Filtres</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Sélecteur de produit avec recherche */}
              <div className="relative">
                <label className="text-xs text-gray-500 mb-1 block">Produit</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Tous les produits..."
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setShowProductDropdown(true);
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-8"
                  />
                  {selectedProductId && (
                    <button
                      onClick={() => { setSelectedProductId(''); setProductSearch(''); }}
                      className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                    >×</button>
                  )}
                </div>
                {showProductDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <button
                      onClick={() => { setSelectedProductId(''); setProductSearch(''); setShowProductDropdown(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-gray-500"
                    >
                      Tous les produits
                    </button>
                    {movementProducts
                      .filter(p => !productSearch || p.nom?.toLowerCase().includes(productSearch.toLowerCase()))
                      .map(p => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setSelectedProductId(p.id);
                            setProductSearch(p.nom);
                            setShowProductDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center justify-between ${selectedProductId === p.id ? 'bg-indigo-50 text-indigo-700' : ''}`}
                        >
                          <span className="truncate">{p.nom}</span>
                          <span className="text-xs text-gray-400 ml-2 shrink-0">{p.nb_mouvements} mvt{p.nb_mouvements > 1 ? 's' : ''}</span>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>

              {/* Type de mouvement */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Type</label>
                <select
                  value={movementTypeFilter}
                  onChange={(e) => setMovementTypeFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Tous les types</option>
                  <option value="entree">↓ Entrées (ravitaillement, retours)</option>
                  <option value="sortie">↑ Sorties (ventes, factures)</option>
                  <option value="ajustement">↔ Ajustements</option>
                </select>
              </div>

              {/* Date début */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Date début</label>
                <input
                  type="date"
                  value={movementDateDebut}
                  onChange={(e) => setMovementDateDebut(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Date fin */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Date fin</label>
                <input
                  type="date"
                  value={movementDateFin}
                  onChange={(e) => setMovementDateFin(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => loadMovements(1)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
              >
                <Search size={14} className="inline mr-1" /> Rechercher
              </button>
              <button
                onClick={() => {
                  setSelectedProductId('');
                  setProductSearch('');
                  setMovementTypeFilter('');
                  setMovementDateDebut('');
                  setMovementDateFin('');
                  setTimeout(() => loadMovements(1), 100);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
              >
                <RotateCcw size={14} className="inline mr-1" /> Réinitialiser
              </button>
            </div>
          </div>

          {/* Stats résumé du produit sélectionné */}
          {movementData?.stats && selectedProductId && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
                <div className="text-xs text-green-600">Total entrées</div>
                <div className="text-xl font-bold text-green-700">{formatNumber(movementData.stats.total_entrees || 0)}</div>
                <div className="text-xs text-green-500">{movementData.stats.nb_entrees || 0} opération{(movementData.stats.nb_entrees || 0) > 1 ? 's' : ''}</div>
              </div>
              <div className="bg-red-50 rounded-xl p-3 border border-red-200 text-center">
                <div className="text-xs text-red-600">Total sorties</div>
                <div className="text-xl font-bold text-red-700">{formatNumber(movementData.stats.total_sorties || 0)}</div>
                <div className="text-xs text-red-500">{movementData.stats.nb_sorties || 0} opération{(movementData.stats.nb_sorties || 0) > 1 ? 's' : ''}</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 text-center">
                <div className="text-xs text-blue-600">Premier mouvement</div>
                <div className="text-sm font-bold text-blue-700">
                  {movementData.stats.premier_mouvement
                    ? new Date(movementData.stats.premier_mouvement).toLocaleDateString('fr-FR')
                    : '-'}
                </div>
              </div>
              <div className="bg-purple-50 rounded-xl p-3 border border-purple-200 text-center">
                <div className="text-xs text-purple-600">Dernier mouvement</div>
                <div className="text-sm font-bold text-purple-700">
                  {movementData.stats.dernier_mouvement
                    ? new Date(movementData.stats.dernier_mouvement).toLocaleDateString('fr-FR')
                    : '-'}
                </div>
              </div>
            </div>
          )}

          {/* Liste des mouvements */}
          {movementLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
          ) : movementData?.movements?.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {movementData.total} mouvement{movementData.total > 1 ? 's' : ''} trouvé{movementData.total > 1 ? 's' : ''}
                  {movementData.total_pages > 1 && ` — Page ${movementData.page} sur ${movementData.total_pages}`}
                </p>
              </div>

              {/* Version desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Date</th>
                      <th className="text-left px-3 py-3 font-medium">Produit</th>
                      <th className="text-center px-3 py-3 font-medium">Direction</th>
                      <th className="text-center px-3 py-3 font-medium">Type</th>
                      <th className="text-right px-3 py-3 font-medium">Quantité</th>
                      <th className="text-right px-3 py-3 font-medium">Stock avant</th>
                      <th className="text-right px-3 py-3 font-medium">Stock après</th>
                      <th className="text-left px-3 py-3 font-medium">Référence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {movementData.movements.map((m) => (
                      <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {new Date(m.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          <br />
                          <span className="text-gray-400">
                            {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center space-x-2">
                            {m.product_image ? (
                              <img src={m.product_image} alt="" className="w-7 h-7 rounded object-cover" />
                            ) : (
                              <div className="w-7 h-7 rounded bg-gray-200 flex items-center justify-center"><Package size={14} /></div>
                            )}
                            <div>
                              <div className="font-medium text-gray-800 text-xs">{m.product_nom}</div>
                              {m.product_categorie && <div className="text-xs text-gray-400">{m.product_categorie}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="text-center px-3 py-3">
                          {m.direction === 'entree' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              <ArrowDownCircle size={12} /> Entrée
                            </span>
                          ) : m.direction === 'sortie' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              <ArrowUpCircle size={12} /> Sortie
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              <RotateCcw size={12} /> Ajust.
                            </span>
                          )}
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className="text-xs text-gray-600">{m.type_label}</span>
                        </td>
                        <td className="text-right px-3 py-3">
                          <span className={`font-bold ${m.direction === 'entree' ? 'text-green-600' : m.direction === 'sortie' ? 'text-red-600' : 'text-gray-700'}`}>
                            {m.direction === 'entree' ? '+' : m.direction === 'sortie' ? '-' : ''}{m.quantite}
                          </span>
                        </td>
                        <td className="text-right px-3 py-3 text-gray-500">{m.stock_avant}</td>
                        <td className="text-right px-3 py-3 font-semibold text-gray-800">{m.stock_apres}</td>
                        <td className="px-3 py-3">
                          {m.reference_label ? (
                            <div>
                              {m.reference_id && m.reference_type && ['invoice', 'facture', 'avoir'].includes(m.reference_type) ? (
                                <Link to={`${routeBase}/invoices/${m.reference_id}`} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
                                  {m.reference_label} ↗
                                </Link>
                              ) : m.reference_id && m.reference_type && ['restock', 'ravitaillement'].includes(m.reference_type) ? (
                                <Link to={`${routeBase}/restock/history?id=${m.reference_id}`} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
                                  {m.reference_label} ↗
                                </Link>
                              ) : (
                                <div className="text-xs font-medium text-indigo-600">{m.reference_label}</div>
                              )}
                              {m.client_nom && <div className="text-xs text-gray-400">Client: {m.client_nom}</div>}
                              {m.restock_notes && <div className="text-xs text-gray-400">{m.restock_notes}</div>}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version mobile */}
              <div className="md:hidden divide-y divide-gray-100">
                {movementData.movements.map((m) => (
                  <div key={m.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {m.product_image ? (
                          <img src={m.product_image} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center"><Package size={16} /></div>
                        )}
                        <div>
                          <div className="font-medium text-gray-800 text-sm">{m.product_nom}</div>
                          <div className="text-xs text-gray-400">
                            {new Date(m.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                      </div>
                      {m.direction === 'entree' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          <ArrowDownCircle size={12} /> +{m.quantite}
                        </span>
                      ) : m.direction === 'sortie' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          <ArrowUpCircle size={12} /> -{m.quantite}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                          <RotateCcw size={12} /> {m.quantite}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">{m.type_label}</span>
                      <span className="text-gray-500">
                        Stock: {m.stock_avant} → <strong>{m.stock_apres}</strong>
                      </span>
                    </div>
                    {m.reference_label && (
                      <div className="mt-1">
                        {m.reference_id && m.reference_type && ['invoice', 'facture', 'avoir'].includes(m.reference_type) ? (
                          <Link to={`${routeBase}/invoices/${m.reference_id}`} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
                            {m.reference_label} ↗
                          </Link>
                        ) : m.reference_id && m.reference_type && ['restock', 'ravitaillement'].includes(m.reference_type) ? (
                          <Link to={`${routeBase}/restock/history?id=${m.reference_id}`} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
                            {m.reference_label} ↗
                          </Link>
                        ) : (
                          <span className="text-xs font-medium text-indigo-600">{m.reference_label}</span>
                        )}
                      </div>
                    )}
                    {m.client_nom && <div className="text-xs text-gray-400">Client: {m.client_nom}</div>}
                    {m.restock_notes && <div className="text-xs text-gray-400">{m.restock_notes}</div>}
                  </div>
                ))}
              </div>

              {/* Pagination mouvements */}
              {movementData.total_pages > 1 && (
                <div className="flex justify-center items-center gap-2 py-4 border-t border-gray-100">
                  <button
                    onClick={() => loadMovements(1)}
                    disabled={movementPage === 1}
                    className={`px-3 py-2 rounded-lg text-sm ${movementPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                  >
                    ««
                  </button>
                  <button
                    onClick={() => loadMovements(movementPage - 1)}
                    disabled={movementPage === 1}
                    className={`px-4 py-2 rounded-lg text-sm ${movementPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                  >
                    ← Préc.
                  </button>
                  <span className="text-sm text-gray-600 px-3">
                    {movementData.page} / {movementData.total_pages}
                  </span>
                  <button
                    onClick={() => loadMovements(movementPage + 1)}
                    disabled={movementPage === movementData.total_pages}
                    className={`px-4 py-2 rounded-lg text-sm ${movementPage === movementData.total_pages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                  >
                    Suiv. →
                  </button>
                  <button
                    onClick={() => loadMovements(movementData.total_pages)}
                    disabled={movementPage === movementData.total_pages}
                    className={`px-3 py-2 rounded-lg text-sm ${movementPage === movementData.total_pages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                  >
                    »»
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm">
              <div className="text-4xl mb-3"><History size={40} className="mx-auto" /></div>
              <p className="font-medium">Aucun mouvement trouvé</p>
              <p className="text-sm mt-1">
                {selectedProductId || movementTypeFilter || movementDateDebut
                  ? 'Essayez de modifier vos filtres'
                  : 'Les mouvements de stock apparaîtront ici (ventes, ravitaillements, retours...)'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>

    {/* === CONTENU IMPRESSION (caché à l'écran) === */}
    <div style={{ display: 'none' }}>
      <div ref={printRef}>
        <div style={{ fontFamily: 'Arial, sans-serif', padding: '20px', color: '#333' }}>
          {/* En-tête entreprise */}
          <div style={{ borderBottom: '3px solid #4F46E5', paddingBottom: '20px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              {settings?.logo && (
                <img
                  src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                  alt="Logo"
                  style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '8px' }}
                />
              )}
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 'bold', margin: '0 0 4px 0', color: '#1F2937' }}>
                  {settings?.nom_entreprise || 'Mon Entreprise'}
                </h2>
                {settings?.adresse && <p style={{ margin: '2px 0', fontSize: '13px', color: '#6B7280' }}>{settings.adresse}</p>}
                {settings?.telephone && <p style={{ margin: '2px 0', fontSize: '13px', color: '#6B7280' }}><Phone size={13} className="inline mr-1" /> {settings.telephone}</p>}
                {settings?.email && <p style={{ margin: '2px 0', fontSize: '13px', color: '#6B7280' }}><Mail size={13} className="inline mr-1" /> {settings.email}</p>}
                {settings?.numero_registre && <p style={{ margin: '2px 0', fontSize: '11px', color: '#9CA3AF' }}>RCCM: {settings.numero_registre}</p>}
                {settings?.numero_fiscal && <p style={{ margin: '2px 0', fontSize: '11px', color: '#9CA3AF' }}>NIF: {settings.numero_fiscal}</p>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#4F46E5', margin: '0' }}>ANALYSE DES VENTES</h1>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0' }}>
                Période : {periodOptions.find(p => p.value === period)?.label || period}
              </p>
              <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '2px 0' }}>
                Généré le {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Résumé stats */}
          {flowData?.stats && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '120px', background: '#F0FDF4', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#16A34A' }}>Best-sellers</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#15803D' }}>{flowData.stats.best_sellers}</div>
              </div>
              <div style={{ flex: 1, minWidth: '120px', background: '#EFF6FF', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#2563EB' }}>Bons</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1D4ED8' }}>{flowData.stats.bons}</div>
              </div>
              <div style={{ flex: 1, minWidth: '120px', background: '#FFFBEB', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#D97706' }}>Moyens</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#B45309' }}>{flowData.stats.moyens}</div>
              </div>
              <div style={{ flex: 1, minWidth: '120px', background: '#FFF7ED', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#EA580C' }}>Lents</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#C2410C' }}>{flowData.stats.lents}</div>
              </div>
              <div style={{ flex: 1, minWidth: '120px', background: '#FEF2F2', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#DC2626' }}>Dormants</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#B91C1C' }}>{flowData.stats.dormants}</div>
              </div>
              <div style={{ flex: 1, minWidth: '120px', background: '#F3F4F6', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#6B7280' }}>Jamais vendus</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4B5563' }}>{flowData.stats.jamais_vendus}</div>
              </div>
            </div>
          )}

          {/* Résumé financier */}
          {flowData?.stats && (
            <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', padding: '14px', background: '#F5F3FF', borderRadius: '8px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#7C3AED' }}>Quantité vendue</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatNumber(flowData.stats.total_vendu)}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#7C3AED' }}>Chiffre d'affaires</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatMoney(flowData.stats.total_ca)}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#059669' }}>Bénéfice estimé</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#059669' }}>{formatMoney(flowData.stats.total_benefice)}</div>
              </div>
            </div>
          )}

          {/* Tableau complet (tous les produits, sans pagination) */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#F3F4F6' }}>
                <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>#</th>
                <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>Produit</th>
                <th style={{ textAlign: 'center', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>Statut</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>Vendus</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>CA</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>Marge</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>Bénéfice</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>Part %</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid #E5E7EB' }}>Stock</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredProducts().map((product, index) => (
                <tr key={product.product_id || index} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '6px', color: '#9CA3AF' }}>{index + 1}</td>
                  <td style={{ padding: '6px' }}>
                    <strong>{product.product_name || 'Produit supprimé'}</strong>
                    {product.categorie && <span style={{ fontSize: '10px', color: '#9CA3AF', marginLeft: '6px' }}>({product.categorie})</span>}
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', backgroundColor: product.classification_color + '20', color: product.classification_color }}>
                      {product.classification_label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px', fontWeight: '600' }}>{formatNumber(product.total_vendu)}</td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>{formatMoney(product.total_ca)}</td>
                  <td style={{ textAlign: 'right', padding: '6px', color: product.marge_pct >= 30 ? '#16A34A' : product.marge_pct >= 15 ? '#D97706' : '#DC2626' }}>
                    {product.marge_pct}%
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>{formatMoney(product.benefice_total)}</td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>{product.part_ventes}%</td>
                  <td style={{ textAlign: 'right', padding: '6px', color: product.stock_actuel <= (product.stock_minimum || 0) ? '#DC2626' : '#374151' }}>
                    {product.stock_actuel ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pied de page impression */}
          <div style={{ marginTop: '30px', paddingTop: '16px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9CA3AF' }}>
            <span>{settings?.nom_entreprise || 'Mon Entreprise'} — Analyse des ventes</span>
            <span>Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default BusinessSalesAnalytics;
